import type {
  ApiCheckoutMethod,
  ApiMobileAppPolicy,
  BindCheckoutPolicyRequest,
  BoundCheckoutPolicy,
  CheckoutPolicy,
  CheckoutSurface,
  PaymentSession,
  ZippyMobileAppConfig,
} from '../types';
import { detectCheckoutSurface } from './device';

export type EntryRoutingAction = 'chooser' | 'auto-qr' | 'auto-manual' | 'enforce-mobile-app';

export type MethodAvailability = {
  qr: boolean;
  manual: boolean;
  mobileApp: boolean;
};

export type ResolvedCheckoutUiOptions = {
  checkoutPolicy: BoundCheckoutPolicy;
  mobileAppPolicy: ApiMobileAppPolicy;
  surface: CheckoutSurface;
  methodAvailability: MethodAvailability;
  allowManualBack: boolean;
};

export type AppClaimCheckoutMethod = Extract<ApiCheckoutMethod, 'QR' | 'MOBILE_APP'>;

export function isCheckoutPolicyBound(session: PaymentSession): boolean {
  return session.checkoutPolicy != null || session.mobileAppPolicy != null;
}

export function isFullChooserPolicy(checkoutPolicy: BoundCheckoutPolicy): boolean {
  return checkoutPolicy == null;
}

export function allowsQrCheckoutPolicy(checkoutPolicy: BoundCheckoutPolicy): boolean {
  return checkoutPolicy == null || checkoutPolicy === 'QR';
}

export function allowsManualCheckoutPolicy(checkoutPolicy: BoundCheckoutPolicy): boolean {
  return checkoutPolicy == null || checkoutPolicy === 'MANUAL';
}

/** Phone + ENFORCE → app-only auto-start for full chooser, QR, or MANUAL bind. */
function phoneEnforceAutoApp(
  checkoutPolicy: BoundCheckoutPolicy,
  mobileAppPolicy: ApiMobileAppPolicy,
): boolean {
  return (
    mobileAppPolicy === 'ENFORCE' &&
    (checkoutPolicy == null || checkoutPolicy === 'QR' || checkoutPolicy === 'MANUAL')
  );
}

export function resolveMobileAppPolicy(mobileApp?: ZippyMobileAppConfig): ApiMobileAppPolicy {
  if (mobileApp?.policy === 'off') {
    return 'OFF';
  }
  if (mobileApp?.policy === 'option') {
    return 'OPTION';
  }
  if (mobileApp?.policy === 'enforce') {
    return 'ENFORCE';
  }
  return 'ENFORCE';
}

export function buildBindPolicyRequest(config: {
  checkoutPolicy?: CheckoutPolicy;
  mobileApp?: ZippyMobileAppConfig;
}): BindCheckoutPolicyRequest {
  const request: BindCheckoutPolicyRequest = {};

  if (config.checkoutPolicy === 'QR' || config.checkoutPolicy === 'MANUAL') {
    request.checkoutPolicy = config.checkoutPolicy;
  }

  const mobileAppPolicy = resolveMobileAppPolicy(config.mobileApp);
  if (mobileAppPolicy !== 'OFF') {
    request.mobileAppPolicy = mobileAppPolicy;
  }

  return request;
}

/** Web fallbacks shown after a failed app deep-link on phone. */
export function resolveFallbackMethodAvailability(
  checkoutPolicy: BoundCheckoutPolicy,
): Pick<MethodAvailability, 'qr' | 'manual'> {
  return {
    qr: allowsQrCheckoutPolicy(checkoutPolicy),
    manual: allowsManualCheckoutPolicy(checkoutPolicy),
  };
}

export function resolveMethodAvailability(
  checkoutPolicy: BoundCheckoutPolicy,
  mobileAppPolicy: ApiMobileAppPolicy,
  surface: CheckoutSurface,
): MethodAvailability {
  if (surface === 'desktop') {
    return {
      qr: allowsQrCheckoutPolicy(checkoutPolicy),
      manual: allowsManualCheckoutPolicy(checkoutPolicy),
      mobileApp: false,
    };
  }

  const autoAppOnly = phoneEnforceAutoApp(checkoutPolicy, mobileAppPolicy);

  return {
    qr: allowsQrCheckoutPolicy(checkoutPolicy) && !autoAppOnly,
    manual: allowsManualCheckoutPolicy(checkoutPolicy) && !autoAppOnly,
    mobileApp:
      autoAppOnly ||
      (mobileAppPolicy === 'OPTION' &&
        (checkoutPolicy == null ||
          checkoutPolicy === 'QR' ||
          checkoutPolicy === 'MANUAL')),
  };
}

export function resolveEntryRouting(
  checkoutPolicy: BoundCheckoutPolicy,
  mobileAppPolicy: ApiMobileAppPolicy,
  surface: CheckoutSurface,
): EntryRoutingAction {
  if (surface === 'desktop') {
    if (checkoutPolicy === 'MANUAL') {
      return 'auto-manual';
    }
    if (checkoutPolicy === 'QR') {
      return 'auto-qr';
    }
    return 'chooser';
  }

  if (phoneEnforceAutoApp(checkoutPolicy, mobileAppPolicy)) {
    return 'enforce-mobile-app';
  }

  if (checkoutPolicy === 'MANUAL' && mobileAppPolicy === 'OFF') {
    return 'auto-manual';
  }

  if (checkoutPolicy === 'QR' && mobileAppPolicy === 'OFF') {
    return 'auto-qr';
  }

  return 'chooser';
}

export function isCheckoutStartAllowed(
  checkoutPolicy: BoundCheckoutPolicy,
  mobileAppPolicy: ApiMobileAppPolicy,
  surface: CheckoutSurface,
  method: AppClaimCheckoutMethod,
): boolean {
  if (method === 'MOBILE_APP') {
    if (surface === 'desktop') {
      return false;
    }
    if (mobileAppPolicy === 'OFF') {
      return false;
    }
    if (phoneEnforceAutoApp(checkoutPolicy, mobileAppPolicy)) {
      return true;
    }
    if (mobileAppPolicy === 'OPTION') {
      return (
        checkoutPolicy == null || checkoutPolicy === 'QR' || checkoutPolicy === 'MANUAL'
      );
    }
    return false;
  }

  if (!allowsQrCheckoutPolicy(checkoutPolicy)) {
    return false;
  }

  if (surface === 'phone' && phoneEnforceAutoApp(checkoutPolicy, mobileAppPolicy)) {
    return false;
  }

  return true;
}

export function resolveAutoStartMethod(
  checkoutPolicy: BoundCheckoutPolicy,
  mobileAppPolicy: ApiMobileAppPolicy,
  surface: CheckoutSurface,
): AppClaimCheckoutMethod | null {
  const routing = resolveEntryRouting(checkoutPolicy, mobileAppPolicy, surface);

  if (routing === 'enforce-mobile-app') {
    return isCheckoutStartAllowed(checkoutPolicy, mobileAppPolicy, surface, 'MOBILE_APP')
      ? 'MOBILE_APP'
      : null;
  }

  if (routing === 'auto-qr') {
    return isCheckoutStartAllowed(checkoutPolicy, mobileAppPolicy, surface, 'QR') ? 'QR' : null;
  }

  return null;
}

export function resolveCheckoutUiOptionsFromBound(
  checkoutPolicy: BoundCheckoutPolicy,
  mobileAppPolicy: ApiMobileAppPolicy,
  surface: CheckoutSurface = detectCheckoutSurface(),
): ResolvedCheckoutUiOptions {
  return {
    checkoutPolicy,
    mobileAppPolicy,
    surface,
    methodAvailability: resolveMethodAvailability(checkoutPolicy, mobileAppPolicy, surface),
    allowManualBack: checkoutPolicy == null,
  };
}

export function resolveCheckoutUiOptionsFromSession(
  session: PaymentSession,
  surface: CheckoutSurface = detectCheckoutSurface(),
): ResolvedCheckoutUiOptions {
  if (!isCheckoutPolicyBound(session)) {
    throw new Error('Checkout policy is not bound on the payment session.');
  }

  return resolveCheckoutUiOptionsFromBound(
    session.checkoutPolicy,
    session.mobileAppPolicy ?? 'OFF',
    surface,
  );
}

export function resolveCheckoutUiOptions(
  checkoutPolicy?: CheckoutPolicy,
  mobileApp?: ZippyMobileAppConfig,
  surface: CheckoutSurface = detectCheckoutSurface(),
): ResolvedCheckoutUiOptions {
  const bind = buildBindPolicyRequest({ checkoutPolicy, mobileApp });
  return resolveCheckoutUiOptionsFromBound(
    bind.checkoutPolicy ?? null,
    bind.mobileAppPolicy ?? 'OFF',
    surface,
  );
}
